import React from "react";
import { formatMonthShort, formatMonthLong } from "../utils/months";

interface MonthComparisonCardProps {
  /** Selected-month consumed (current-month view: through today; past-month view: full month) */
  consumedThisPeriod: number;
  /** Previous-month consumed compared on the same basis (through same DOM, or full month) */
  consumedPreviousPeriod: number;
  /** The selected month label (used in current-month subtitle clarity) */
  selectedMonth: string;
  /** Previous month label, e.g. "2026-04" */
  previousMonth: string;
  /** Previous month full total — for past-month case, identical to consumedPreviousPeriod */
  previousMonthFullTotal: number;
  /** Whether the selected month is the current calendar month */
  isCurrentMonth: boolean;
}

/** Comparison vs previous month.
 *
 * - Current month: compares this month's consumed-to-today against previous
 *   month's consumed-to-same-DOM. Subtitle shows both numerator and
 *   denominator so the percentage is verifiable at a glance.
 * - Past month: compares full selected month vs full previous month. Subtitle
 *   shows the previous month's settled total.
 */
export function MonthComparisonCard({
  consumedThisPeriod,
  consumedPreviousPeriod,
  selectedMonth,
  previousMonth,
  previousMonthFullTotal,
  isCurrentMonth,
}: MonthComparisonCardProps) {
  const fmt = (n: number) =>
    n.toLocaleString(undefined, { maximumFractionDigits: 2 });

  const hasBaseline = consumedPreviousPeriod > 0;
  const delta = hasBaseline
    ? ((consumedThisPeriod - consumedPreviousPeriod) /
        consumedPreviousPeriod) *
      100
    : 0;
  const up = delta > 0;

  // Up >25% reads as concerning; otherwise neutral. Down is neutral.
  const arrowColor = !hasBaseline
    ? "text-muted-foreground"
    : up && delta > 25
    ? "text-warning"
    : "text-muted-foreground";

  // Today's day-of-month, used to label "through May 28" lines on the
  // current-month view.
  const todayDay = isCurrentMonth ? new Date().getUTCDate() : null;
  const ordinal = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm h-full">
      <div className="p-5 flex flex-col">
        <h2 className="text-lg font-semibold text-muted-foreground text-sm uppercase">
          vs {formatMonthShort(previousMonth)}
        </h2>
        <div className="mt-2 flex-1 flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-end mb-2">
              {/* Arrow stays in the body font — it's a glyph, not a figure, and
                  mono's fixed advance width opens an odd gap after it. */}
              <span className={`text-3xl font-bold ${arrowColor}`}>
                {hasBaseline ? (
                  <>
                    {up ? "↑" : "↓"}{" "}
                    <span className="font-mono">
                      {Math.abs(delta).toFixed(0)}%
                    </span>
                  </>
                ) : (
                  "—"
                )}
              </span>
              {isCurrentMonth && todayDay !== null && (
                <span className="text-sm text-muted-foreground mb-1">
                  through {ordinal(todayDay)}
                </span>
              )}
            </div>
            {hasBaseline ? (
              <div className="text-xs text-muted-foreground space-y-0.5 leading-snug">
                {isCurrentMonth ? (
                  <>
                    <div>
                      {fmt(consumedThisPeriod)} in {formatMonthLong(selectedMonth)} to
                      date
                    </div>
                    <div>
                      {fmt(consumedPreviousPeriod)} in {formatMonthLong(previousMonth)}{" "}
                      through {ordinal(todayDay!)}
                    </div>
                    <div className="pt-1">
                      Full {formatMonthShort(previousMonth)}:{" "}
                      {fmt(previousMonthFullTotal)}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      {fmt(consumedThisPeriod)} in {formatMonthLong(selectedMonth)}
                    </div>
                    <div>
                      {fmt(consumedPreviousPeriod)} in {formatMonthLong(previousMonth)}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                No usage in {formatMonthLong(previousMonth)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
